import React, { useEffect, useRef, useState } from "react";
import {
  FaInstagram,
  FaFacebookF,
  FaTiktok,
  FaArrowRight,
  FaArrowLeft,
  FaPlus,
  FaTimes,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";
import { copy, collection } from "../data/boutique";
import Reviews from "./Reviews";
import logo from "../assets/logo.jpg";

const maps =
  "https://www.google.com/maps/dir/?api=1&destination=Maya%27s+Flavours,+Lefkosias+11,+Latsia,+Cyprus";
const instagram = "https://www.instagram.com/mayas_flavours/";

function Photo({ name, alt, className = "", priority = false }) {
  return (
    <img
      className={className}
      src={`/images/${name}-960.webp`}
      srcSet={[480, 960, 1440]
        .map((w) => `/images/${name}-${w}.webp ${w}w`)
        .join(", ")}
      sizes="(max-width: 700px) 100vw, 50vw"
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      width="960"
      height="1280"
    />
  );
}

function Gallery({ c, language }) {
  const [selected, setSelected] = useState(null);
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const images = collection;
  const isOpen = selected !== null;
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      triggerRef.current?.focus();
    };
  }, [isOpen]);
  const move = (direction) =>
    setSelected((i) => (i + direction + images.length) % images.length);
  return (
    <section
      className="collection section-wrap"
      id="gallery"
      aria-labelledby="collection-title"
    >
      <div className="section-heading">
        <div>
          <p className="eyebrow">02 / {c.collectionLabel}</p>
          <h2 id="collection-title">
            {c.collectionTitle}
            <br />
            <em>{c.collectionAccent}</em>
          </h2>
        </div>
        <p>{c.collectionIntro}</p>
      </div>
      <div className="collection-grid">
        {images.map((item, index) => (
          <button
            className="dessert-card"
            key={item.name}
            onClick={(e) => {
              triggerRef.current = e.currentTarget;
              setSelected(index);
            }}
            aria-label={`${c.viewPhoto}: ${item[language]}`}
          >
            <div className="dessert-image">
              {item.archive ? (
                <img
                  src={`/images/${item.name}.webp`}
                  alt={item[language]}
                  loading="lazy"
                  width="960"
                  height="1280"
                />
              ) : (
                <Photo name={item.name} alt={item[language]} />
              )}
              <span className="photo-open" aria-hidden="true">
                <FaPlus />
              </span>
            </div>
          </button>
        ))}
      </div>
      <div className="collection-bottom">
        <span>{c.availability}</span>
      </div>
      {isOpen && (
        <dialog
          className="photo-dialog"
          ref={dialogRef}
          aria-label={c.viewPhoto}
          onCancel={() => setSelected(null)}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") move(1);
            if (e.key === "ArrowLeft") move(-1);
          }}
        >
          <button
            className="dialog-close"
            onClick={() => setSelected(null)}
            aria-label={c.close}
          >
            <FaTimes />
          </button>
          <img
            src={`/images/${images[selected].name}${images[selected].archive ? "" : "-1440"}.webp`}
            alt={images[selected][language]}
          />
          <div className="dialog-controls">
            <button onClick={() => move(-1)} aria-label={c.previous}>
              <FaArrowLeft />
            </button>
            <p aria-live="polite">
              {images[selected][language]} · {selected + 1} / {images.length}
            </p>
            <button onClick={() => move(1)} aria-label={c.next}>
              <FaArrowRight />
            </button>
          </div>
        </dialog>
      )}
    </section>
  );
}

export default function Boutique() {
  const { language, toggleLanguage, t } = useLanguage();
  const c = copy[language];
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="boutique">
      <a className="skip-link" href="#main">
        {c.skip}
      </a>
      <div className="announcement">
        {c.announcement}
        <span aria-hidden="true">✦</span>
        <a href="#contact">{c.visit}</a>
      </div>
      <header className="site-header">
        <a className="wordmark" href="#home" aria-label="Maya's Flavours">
          <img src={logo} alt="" width="48" height="48" />
          <span>
            Maya’s Flavours<small>{c.wordmark}</small>
          </span>
        </a>
        <nav
          className={menuOpen ? "main-nav is-open" : "main-nav"}
          id="main-nav"
          aria-label={c.navigation}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setMenuOpen(false);
              document.getElementById("menu-toggle").focus();
            }
          }}
        >
          {["testimonials", "gallery", "about", "contact"].map((id) => (
            <a href={`#${id}`} key={id} onClick={() => setMenuOpen(false)}>
              {id === "gallery" ? c.collectionLabel : t(`nav.${id}`)}
            </a>
          ))}
        </nav>
        <div className="header-actions">
          <button
            className="language-switch"
            onClick={toggleLanguage}
            aria-label={
              language === "el" ? "Switch to English" : "Αλλαγή σε Ελληνικά"
            }
          >
            <span className={language === "el" ? "chosen" : ""}>ΕΛ</span>
            <span aria-hidden="true">/</span>
            <span className={language === "en" ? "chosen" : ""}>EN</span>
          </button>
          <a className="header-call" href="tel:+35796420156">
            <FaPhone aria-hidden="true" /> {c.call}
          </a>
          <button
            id="menu-toggle"
            className="menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="main-nav"
            aria-label={c.menu}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <FaTimes /> : <span>☰</span>}
          </button>
        </div>
      </header>
      <main id="main">
        <div className="hero-backdrop">
          <section
            className="editorial-hero section-wrap"
            id="home"
            aria-labelledby="hero-title"
          >
            <div className="hero-copy">
              <p className="eyebrow">
                <span className="tiny-star" aria-hidden="true">
                  ✦
                </span>{" "}
                {c.heroLabel}
              </p>
              <h1 id="hero-title">
                {c.heroTitle}
                <br />
                <em>{c.heroAccent}</em>
              </h1>
              <p className="hero-intro">{c.heroIntro}</p>
              <div className="hero-actions">
                <a className="button" href="#gallery">
                  {c.explore} <FaArrowRight />
                </a>
                <a className="underlined-link" href="#contact">
                  {c.visit}
                </a>
              </div>
              <div className="hero-note">
                <span aria-hidden="true">✧</span>
                <p>
                  {c.heroNote}
                  <br />
                  <strong>{c.heroNoteAccent}</strong>
                </p>
              </div>
            </div>
          </section>
        </div>
        <div className="flavour-ribbon" aria-hidden="true">
          <span>Trompe-l’œil</span>
          <i>✦</i>
          <span>{c.ribbon1}</span>
          <i>✦</i>
          <span>{c.ribbon2}</span>
          <i>✦</i>
          <span>Maya’s Flavours</span>
          <i>✦</i>
        </div>
        <Reviews c={c} language={language} />
        <Gallery c={c} language={language} />
        <section className="story" id="about" aria-labelledby="story-title">
          <div className="story-inner section-wrap">
            <div className="story-photo">
              <Photo name="maya" alt={c.mayaAlt} />
              <span className="photo-footnote">{c.storyFootnote}</span>
            </div>
            <div className="story-copy">
              <p className="eyebrow">03 / {c.storyLabel}</p>
              <h2 id="story-title">
                {c.storyTitle}
                <br />
                <em>{c.storyAccent}</em>
              </h2>
              <p>{c.storyIntro}</p>
              <blockquote>“{t("about.quote")}”</blockquote>
              <span className="signature">
                {language === "el" ? "Μάγια Παναγή" : "Maya Panayi"}
              </span>
              <a
                className="text-link"
                href={instagram}
                target="_blank"
                rel="noreferrer"
              >
                {c.followStory} <FaInstagram />
              </a>
            </div>
          </div>
        </section>
        <section
          className="visit section-wrap"
          id="contact"
          aria-labelledby="visit-title"
        >
          <div className="visit-heading">
            <p className="eyebrow">04 / {c.visitLabel}</p>
            <h2 id="visit-title">
              {c.visitTitle}
              <br />
              <em>{c.visitAccent}</em>
            </h2>
            <p>{c.visitIntro}</p>
            <a className="button" href={maps} target="_blank" rel="noreferrer">
              <FaMapMarkerAlt /> {t("contact.findUs")}
            </a>
          </div>
          <div className="visit-details">
            <div>
              <p className="eyebrow">{t("contact.location")}</p>
              <h3>{c.address}</h3>
              <p>{t("contact.locationText")}</p>
            </div>
            <div>
              <p className="eyebrow">{t("contact.hours")}</p>
              <dl>
                <div>
                  <dt>{t("contact.monSat")}</dt>
                  <dd>10:00 – 21:00</dd>
                </div>
                <div>
                  <dt>{t("contact.sunday")}</dt>
                  <dd>{t("contact.closed")}</dd>
                </div>
              </dl>
            </div>
            <div className="visit-phone">
              <div>
                <p className="eyebrow">{c.enquiries}</p>
                <a href="tel:+35796420156">96 420156</a>
              </div>
              <a
                className="phone-circle"
                href="tel:+35796420156"
                aria-label={c.call}
              >
                <FaPhone />
              </a>
            </div>
          </div>
        </section>
        <section className="instagram-strip">
          <div>
            <FaInstagram />
            <p>{c.instagramTitle}</p>
            <a href={instagram} target="_blank" rel="noreferrer">
              @mayas_flavours <FaArrowRight />
            </a>
          </div>
        </section>
      </main>
      <footer className="site-footer section-wrap">
        <div className="footer-top">
          <a className="footer-wordmark" href="#home">
            Maya’s Flavours<span>{c.footerTagline}</span>
          </a>
          <div className="footer-social">
            <a
              href={instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram />
            </a>
            <a
              href="https://www.facebook.com/MayasFlavours"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.tiktok.com/@mayasflavours"
              target="_blank"
              rel="noreferrer"
              aria-label="TikTok"
            >
              <FaTiktok />
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Maya’s Flavours. {t("footer.rights")}.
          </p>
          <p>
            {t("footer.designedBy")}{" "}
            <a
              href="https://pmpnoveltysolutions.com"
              target="_blank"
              rel="noreferrer"
            >
              PMP Novelty Business Solutions
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
